type CompareGetter<T> = { bivarianceHack(value: T): unknown }['bivarianceHack'];
type CompareLeaf<T> = PropertyKey | CompareGetter<T>;
export type CompareKey<T> = CompareLeaf<T> | Array<CompareLeaf<T> | CompareKey<unknown>>;
type UnknownCompareLeaf = CompareLeaf<unknown>;

function isUnknownCompareLeaf(value: CompareKey<unknown>): value is UnknownCompareLeaf {
  return !Array.isArray(value);
}

function getArrayLikeLength(value: unknown): number | undefined {
  if (value == null) {
    return undefined;
  }

  const length = Reflect.get(Object(value), 'length');
  return typeof length === 'number' ? length : undefined;
}

function getArrayLikeItem(value: unknown, index: number): unknown {
  if (value == null) {
    return undefined;
  }

  return Reflect.get(Object(value), index);
}

function getByKey<T>(key: CompareLeaf<T>): (o: T) => unknown;
// eslint-disable-next-line no-redeclare
function getByKey(key: UnknownCompareLeaf): (o: unknown) => unknown;
// eslint-disable-next-line no-redeclare
function getByKey(key: UnknownCompareLeaf): (o: unknown) => unknown {
  return (o: unknown) =>
    typeof key === 'function' ? key(o) : o == null ? undefined : Reflect.get(Object(o), key);
}

export function equalByMany<T>(
  obj1: T | T[] | null | undefined,
  obj2: T | T[] | null | undefined,
  ...compareKeys: Array<CompareKey<T>>
): boolean;
// eslint-disable-next-line no-redeclare
export function equalByMany(
  obj1: unknown,
  obj2: unknown,
  ...compareKeys: Array<CompareKey<unknown>>
): boolean;
// eslint-disable-next-line no-redeclare
export function equalByMany(
  obj1: unknown,
  obj2: unknown,
  ...compareKeys: Array<CompareKey<unknown>>
): boolean {
  if (!obj1 || !obj2) {
    return false;
  }
  return compareKeys.every((compareKey) => {
    if (Array.isArray(compareKey)) {
      const [key, ...keys] = compareKey;
      if (!isUnknownCompareLeaf(key)) {
        return false;
      }

      const get = getByKey(key);
      if (Array.isArray(obj1)) {
        const otherLength = getArrayLikeLength(obj2);
        return (
          otherLength === obj1.length &&
          obj1.length &&
          obj1.every((o, i) => equalByMany(get(o), get(getArrayLikeItem(obj2, i)), ...keys))
        );
      }
      return equalByMany(get(obj1), get(obj2), ...keys);
    }

    if (!isUnknownCompareLeaf(compareKey)) {
      return false;
    }

    const get = getByKey(compareKey);
    if (Array.isArray(obj1)) {
      const otherLength = getArrayLikeLength(obj2);
      return (
        otherLength === obj1.length &&
        (!obj1.length || obj1.every((o, i) => get(o) === get(getArrayLikeItem(obj2, i))))
      );
    }
    return get(obj1) === get(obj2);
  });
}

export function comparerBy<T>(comparer: keyof T | ((o: T) => unknown)): (arr1: T[], arr2: T[]) => boolean {
  return (arr1: T[], arr2: T[]) => equalBy(arr1, arr2, comparer);
}

export function equalBy<T>(arr1: T[], arr2: T[], comparer: keyof T | ((o: T) => unknown)): boolean {
  if (arr1 === arr2) {
    return true;
  }

  if (arr1.length !== arr2.length) {
    return false;
  }

  const get = getByKey(comparer);
  const keys = new Set(arr2.map(get));

  return arr1.every((item) => keys.has(get(item)));
}

export function distribute<T>(
  array: T[],
  predicate: (item: T, ...args: unknown[]) => boolean,
  thisArg?: unknown,
): [T[], T[]] {
  return [array.filter(predicate, thisArg), array.filter((...args) => !predicate(...args), thisArg)];
}

type FieldsOfType<T, V> = { [K in keyof T]: NonNullable<T[K]> extends V ? K : never }[keyof T];

export function groupByField<T extends object>(
  array: T[],
  field: FieldsOfType<T, PropertyKey>,
): Record<string, T[]> {
  return array.reduce<Record<string, T[]>>((map, value) => {
    const groupKey = String(value[field]);
    if (!map[groupKey]) {
      map[groupKey] = [];
    }
    map[groupKey].push(value);
    return map;
  }, {});
}

export function map<T, R>(
  obj: T[] | Record<string, T> | null | undefined,
  callback: (value: T, key: number | string, source: T[] | Record<string, T>) => R,
): R[] | null | undefined {
  if (Array.isArray(obj)) {
    return obj.map(callback);
  }

  if (obj && typeof obj === 'object') {
    return Object.entries(obj).map(([key, value]) => callback(value, key, obj));
  }

  return obj;
}

export function uniqueStrings(values: string[]): string[] {
  const uniqueValues: string[] = [];
  const seen: Record<string, boolean> = Object.create(null);

  values.forEach((value) => {
    if (seen[value]) {
      return;
    }

    seen[value] = true;
    uniqueValues.push(value);
  });

  return uniqueValues;
}

export function pickObjectKeys<T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Partial<Pick<T, K>> {
  const result: Partial<Pick<T, K>> = {};

  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }

  return result;
}

export function uniqBy<T>(array: T[], key: keyof T | ((o: T) => unknown)): T[] {
  const seen = new Set<unknown>();
  const get = getByKey(key);

  return array.filter((item) => {
    const value = get(item);
    if (seen.has(value)) {
      return false;
    }

    seen.add(value);
    return true;
  });
}
